import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { api } from '../services/api';
import ProLanding from './ProLanding';
import { Analytics } from '../services/analytics';
import { UserProfile } from '../services/api';

/**
 * How long a PRO purchase lasts. Mirrors UserProfile.PRO_ACCESS_DAYS on the
 * backend, which is what actually enforces it - this is only for copy.
 */
export const PRO_ACCESS_DAYS = 14;

// PRO badge component that can be used throughout the app.
// `sm` (and `hideIcon`) exist for tight spots such as the mobile header nav.
export const ProBadge: React.FC<{
  className?: string;
  size?: 'sm' | 'md';
  hideIcon?: boolean;
}> = ({ className = '', size = 'md', hideIcon = false }) => (
  <span className={`
    inline-flex items-center rounded-full
    font-medium text-white
    bg-clay
    shadow-sm
    ${size === 'sm' ? 'px-1.5 py-0.5 text-[10px] leading-none' : 'px-2.5 py-1 text-xs'}
    ${className}
  `}>
    {!hideIcon && (
      <Sparkles className={size === 'sm' ? 'w-2.5 h-2.5 mr-0.5' : 'w-3 h-3 mr-1'} />
    )}
    PRO
  </span>
);

// Context for managing subscription state
interface SubscriptionContextType {
  isProUser: boolean;
  isLoading: boolean;
  checkProStatus: () => Promise<boolean>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode; userId?: number }> = ({ children, userId }) => {
  const [status, setStatus] = useState<{ userId?: number; active: boolean }>({ active: false });
  const [isLoading, setIsLoading] = useState(true);
  const currentUser = useRef(userId);
  currentUser.current = userId;
  const requestVersion = useRef(0);

  const checkProStatus = useCallback(async () => {
    const version = ++requestVersion.current;
    const token = localStorage.getItem('token');
    const isCurrent = () => currentUser.current === userId && requestVersion.current === version && localStorage.getItem('token') === token;
    if (!userId) {
      setStatus({ active: false });
      setIsLoading(false);
      return false;
    }
    try {
      const response = await api.get('/payments/check-pro-status/');
      if (!isCurrent()) return false;
      const active = response.data.is_pro === true;
      setStatus({ userId, active });
      return active;
    } catch (error) {
      console.error('Error checking PRO status:', error);
      Analytics.logError('Subscription', 'Failed to check PRO status', 'medium');
      if (isCurrent()) setStatus({ userId, active: false });
      return false;
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkProStatus();
    return () => { requestVersion.current += 1; };
  }, [checkProStatus]);

  return (
    <SubscriptionContext.Provider value={{ isProUser: !!userId && status.userId === userId && status.active, isLoading, checkProStatus }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

interface ProtectedFeatureProps {
  children: React.ReactNode;
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

export const ProtectedFeature: React.FC<ProtectedFeatureProps> = ({ 
  children, 
  userProfile, 
  setUserProfile 
}) => {
  const { isProUser, isLoading } = useSubscription();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  // Check if user has access (either PRO or superuser)
  if (userProfile && (userProfile.is_pro || isProUser || userProfile.user?.is_superuser)) {
    return <>{children}</>;
  }

  return <ProLanding userProfile={userProfile} setUserProfile={setUserProfile} />;
};
